import { useCallback, useEffect, useState } from 'react'

import { AxiosError } from 'axios'

import { TGetItemFn } from '@/config/types'
import { CredentialsTypes } from '@/enums/credentialsTypes'
import { PageName } from '@/enums/pageName'
import { CheckoutFlowVersion, PaymentMethod, PaymentPlatform } from '@/enums/payments'

import { ApiService } from '@/api'
import { TLTPData } from '@/api/types'
import { useAuth } from '@/auth/useAuth'
import { useSession } from '@/core/session/useSession'
import { trackAppCredentialsView, trackAppLTPDataView } from '@/events/events'

import { TUserCredentials } from './types'

interface TBaseArgs {
  requestTimeout: number
  retryTimeout: number
  errorRetryTimeout: number
  pageName: PageName
  config: IConfig
}

export interface TUseUserCredentiasArgs extends TBaseArgs {
  credentialsType: CredentialsTypes
  internalPlanId: string | undefined
  paymentMethod: PaymentMethod
  paymentPlatform: PaymentPlatform
}

export interface TUseLTPDataArgs extends TBaseArgs {
  getItem: TGetItemFn
  disabled?: boolean
}

const credentialsTypeToApiCall = {
  [CredentialsTypes.LINK]: (appId: string, ignoreInterceptor401?: boolean) =>
    ApiService.getLinkCredentialsData(appId, ignoreInterceptor401),
  [CredentialsTypes.PASSWORD]: (appId: string, ignoreInterceptor401?: boolean) =>
    ApiService.getPasswordCredentialsData(appId, ignoreInterceptor401),
}

export function useUserCredentials({
  credentialsType,
  requestTimeout,
  retryTimeout,
  errorRetryTimeout,
  pageName,
  internalPlanId,
  paymentMethod,
  paymentPlatform,
  config,
}: TUseUserCredentiasArgs): [
  TUserCredentials | undefined,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Record<string, any> | undefined,
  boolean,
] {
  const { user, logout } = useAuth()
  const sessionContext = useSession()
  const [userCredentials, setUserCredentials] = useState<TUserCredentials | undefined>()
  const [isLoading, setLoading] = useState<boolean>(true)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [errorObj, setErrorObj] = useState<Record<string, any> | undefined>()
  const [startDate] = useState<number>(Date.now())

  const fetchData = useCallback(async (): Promise<NodeJS.Timeout | undefined> => {
    try {
      const getCredentialsFn = credentialsTypeToApiCall[credentialsType]
      const response = await getCredentialsFn(
        config.APP_ID,
        sessionContext.checkoutFlowVersion === CheckoutFlowVersion.V2 // skip 401 redirect if V2
      )

      setErrorObj(undefined)

      if (response.status === 204 && Date.now() - startDate < requestTimeout) {
        return setTimeout(fetchData, retryTimeout)
      } else if (response?.status !== 200 && Date.now() - startDate < requestTimeout) {
        return setTimeout(fetchData, retryTimeout)
      }
      setUserCredentials(response.data)
      setLoading(false)
      trackAppCredentialsView(
        pageName,
        config.PARTNER_ID,
        config.APP_ID,
        config.PAGE_SET_ID,
        sessionContext,
        internalPlanId,
        paymentMethod,
        paymentPlatform,
        user
      )
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error?.response?.status === 401) {
          logout()
          setLoading(false)
          return
        }
        if (error.response?.data) {
          setErrorObj(error.response.data)
        }
      }
      console.error(error)
      if (Date.now() - startDate < requestTimeout) {
        return setTimeout(fetchData, errorRetryTimeout)
      }
      setLoading(false)
    }
  }, [
    startDate,
    logout,
    config,
    user,
    internalPlanId,
    paymentMethod,
    paymentPlatform,
    setLoading,
    credentialsType,
    errorRetryTimeout,
    pageName,
    requestTimeout,
    retryTimeout,
    sessionContext,
  ])

  useEffect(() => {
    if (!user || !internalPlanId) {
      setLoading(false)
      console.log('no fetch')
      return
    }

    console.log('fetch')
    const timeout: { id?: NodeJS.Timeout | undefined } = {}
    fetchData().then((timeoutId) => {
      timeout.id = timeoutId
    })

    return () => {
      if (timeout.id) {
        clearTimeout(timeout.id)
      }
    }
  }, [startDate, user, internalPlanId, fetchData, setLoading])

  return [userCredentials, errorObj, isLoading]
}

export function useLTPData({
  requestTimeout,
  retryTimeout,
  errorRetryTimeout,
  pageName,
  config,
  getItem,
  disabled = false,
}: TUseLTPDataArgs): [
  TLTPData | undefined,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Record<string, any> | undefined,
  boolean,
] {
  const { user, logout } = useAuth()
  const sessionContext = useSession()
  const [ltpData, setLTPData] = useState<TLTPData | undefined>()
  const [isLoading, setLoading] = useState<boolean>(true)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [errorObj, setErrorObj] = useState<Record<string, any> | undefined>()
  const [startDate] = useState<number>(Date.now())

  const paymentMethod = (getItem('ltpPaymentMethod') as PaymentMethod) || undefined
  const paymentPlatform = (getItem('ltpPaymentPlatform') as PaymentPlatform) || undefined

  const fetchData = useCallback(async (): Promise<NodeJS.Timeout | undefined> => {
    try {
      const response = await ApiService.getLTPData(
        config.APP_ID,
        config.PAGE_SET_ID,
        sessionContext.checkoutFlowVersion === CheckoutFlowVersion.V2 // skip 401 redirect if V2
      )

      setErrorObj(undefined)

      if (response.status === 204 && Date.now() - startDate < requestTimeout) {
        return setTimeout(fetchData, retryTimeout)
      } else if (response?.status !== 200 && Date.now() - startDate < requestTimeout) {
        return setTimeout(fetchData, retryTimeout)
      }
      setLTPData(response.data)
      setLoading(false)
      trackAppLTPDataView(
        pageName,
        config.PARTNER_ID,
        config.APP_ID,
        config.PAGE_SET_ID,
        sessionContext,
        response.data.id,
        paymentMethod,
        paymentPlatform,
        user
      )
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error?.response?.status === 401) {
          logout()
          setLoading(false)
          return
        }
        if (error.response?.data) {
          setErrorObj(error.response.data)
        }
      }
      console.error('ltp', error)
      if (Date.now() - startDate < requestTimeout) {
        return setTimeout(fetchData, errorRetryTimeout)
      }
      setLoading(false)
    }
  }, [
    startDate,
    logout,
    config,
    user,
    paymentMethod,
    paymentPlatform,
    setLoading,
    errorRetryTimeout,
    pageName,
    requestTimeout,
    retryTimeout,
    sessionContext,
  ])

  useEffect(() => {
    if (disabled || !user || !config.CONTENT_PRICES?.length) {
      setLoading(false)
      console.log('no fetch ltp')
      return
    }

    console.log('fetch ltp')
    const timeout: { id?: NodeJS.Timeout | undefined } = {}
    fetchData().then((timeoutId) => {
      timeout.id = timeoutId
    })

    return () => {
      if (timeout.id) {
        clearTimeout(timeout.id)
      }
    }
  }, [startDate, user, fetchData, setLoading, config, disabled])

  return [ltpData, errorObj, isLoading]
}
